import { useState } from 'react'

import {
  Member,
  MemberListFilterByOperator,
} from '@tribeplatform/gql-client/types'
import { useAdminOnboarding } from '@tribeplatform/react-components/AdminOnboarding'
import { MemberGroups } from '@tribeplatform/react-components/CMS'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useMembers, useUpdateMember } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { LoadingOverlay } from '@tribeplatform/react-ui-kit/LoadingOverlay'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

const FLAGGED_MEMBERS_LIMIT = 100

export const FlaggedMembers = () => {
  const { $t } = useI18n()
  const { completeStep } = useAdminOnboarding()
  const [loading, setLoading] = useState(false)
  const { mutateAsync: updateMember } = useUpdateMember()
  const { data, isInitialLoading: isLoadingFlaggedMembers } = useMembers({
    fields: {
      profilePicture: 'basic',
    },
    variables: {
      limit: FLAGGED_MEMBERS_LIMIT,
      filterBy: [
        {
          key: 'flagged',
          operator: MemberListFilterByOperator.equals,
          value: 'true',
        },
      ],
    },
    useInfiniteQueryOptions: {
      cacheTime: 0,
    },
  })

  const flaggedMembers = simplifyPaginatedResult<Member>(data)?.nodes ?? []
  const enable = flaggedMembers?.length > 0

  const submitFlaggedMembers = async data => {
    const { flaggedMembers: updatedMembers, enable } = data
    if (!updatedMembers) {
      return
    }

    setLoading(true)

    const flagMemberPromises = updatedMembers
      .filter(
        member =>
          enable &&
          !flaggedMembers.some(flaggedMember => flaggedMember.id === member.id),
      )
      .map(member =>
        updateMember({
          id: member.id,
          input: {
            flagged: true,
          },
        }),
      )

    const unflagMemberPromises = flaggedMembers
      .filter(
        member => !enable || !updatedMembers.some(m => m.id === member.id),
      )
      .map(member =>
        updateMember({
          id: member.id,
          input: {
            flagged: false,
          },
        }),
      )

    try {
      await Promise.all([...flagMemberPromises, ...unflagMemberPromises])
      completeStep({
        step: 'moderation-rules',
      })
      toast({
        title: $t({
          defaultMessage: 'Moderation settings updated.',
          id: 'Admin.ModerationSettings.Updated',
        }),
        status: 'success',
      })
    } catch {
      toast({
        title: $t({
          defaultMessage: 'Unable to update moderation settings.',
          id: 'Admin.ModerationSettings.FailedToUpdate',
        }),
        status: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  if (isLoadingFlaggedMembers) {
    return null
  }

  return (
    <Form
      defaultValues={{
        flaggedMembers,
        enable,
      }}
      onSubmit={submitFlaggedMembers}
    >
      {({ formState: { isDirty }, watch }) => (
        <Card>
          <Card.Header
            title={$t({
              defaultMessage: '{MEMBERS_CC} watchlist',
              id: 'Admin.ModerationSettings.MEMBERSWatchlist.Title',
            })}
            description={$t({
              defaultMessage:
                'Select specific {MEMBERS} to have all their content reviewed before it is made public.',
              id: 'Admin.ModerationSettings.FlaggedMembers.Description',
            })}
          />
          <Card.Content>
            <Form.Layout>
              {data?.pages?.[0]?.totalCount > FLAGGED_MEMBERS_LIMIT && (
                <Alert
                  status="info"
                  className="mb-3"
                  title={$t(
                    {
                      defaultMessage:
                        'You have more than {flagged_members_count, number} flagged {MEMBERS}',
                      id: 'Admin.ModerationSettings.FlaggedMembers.Alert.Title',
                    },
                    {
                      flagged_members_count: FLAGGED_MEMBERS_LIMIT,
                    },
                  )}
                >
                  <T
                    defaultMessage="Some flagged members might not be visible here, please use the {MEMBERS} section."
                    id="Admin.ModerationSettings.FlaggedMembers.Alert.Description"
                  />
                </Alert>
              )}
              <Form.Toggle
                name="enable"
                label={$t({
                  defaultMessage: 'Enable members watchlist',
                  id: 'Admin.ModerationSettings.EnableMembersWatchlist',
                })}
              />
              <LoadingOverlay isLoading={!watch('enable')} pulse={false}>
                <Form.MemberPicker
                  multiple
                  name="flaggedMembers"
                  memberGroup={MemberGroups.member}
                />
              </LoadingOverlay>
              <Form.Actions>
                <Button type="submit" loading={loading} disabled={!isDirty}>
                  <T defaultMessage="Update" id="Generics.Update" />
                </Button>
              </Form.Actions>
            </Form.Layout>
          </Card.Content>
        </Card>
      )}
    </Form>
  )
}
